import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { formatDate } from "@progress/kendo-intl";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Label } from "@progress/kendo-react-labels";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { RadioGroup, Checkbox } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
// clearViewNominals functionality replaced with simple state management
import { addNominal, updateNominal } from "../../services/nominalsService";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import LoadingScreen from "components/common/Loading";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { PageTabItemTitle } from "components/tabs/TabItem/PageTabItemTitle";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import { toast } from "react-toastify";
import Branch from "components/common/Branch";
import { useBranches } from "services/branchService";
import { useActiveVats } from "services/vatService";
import { useActiveCurrencies } from "services/currencyService";
import { useCompany } from "services/companyService";
import {
    AmountFormat,
    formatDateTimeForAPI,
} from "components/common/formatter";
import ClearIcon from "assets/clear-eraser.svg";
import CopyIcon from "assets/copy-previous-year.svg";
import SubmitButton from "components/common/SubmitButton";
import { useAccessRight, AccountsAccessRights } from "../../components/UserAccessRights.ts";

const data = [
    {
        label: "Balance Sheet",
        value: "BalanceSheet",
    },
    {
        label: "Profit & Loss",
        value: "ProfitAndLoss",
    },
];
const BalanceSheet = [
    {
        label: "Office Bank",
        value: "BalanceSheet_OfficeBank",
    },
    {
        label: "Client Bank",
        value: "BalanceSheet_ClientBank",
    },
    {
        label: "DDA Bank",
        value: "BalanceSheet_DDABank",
    },
    {
        label: "Other",
        value: "BalanceSheet_Other",
    },
];
const ProfitAndLoss = [
    {
        label: "Income",
        value: "ProfitAndLoss_Income",
    },
    {
        label: "Expense",
        value: "ProfitAndLoss_Expense",
    },
    {
        label: "Other",
        value: "ProfitAndLoss_Other",
    },
];

const AddNominal = (props) => {
    const [vatsdata, setvatsData] = useState();
    const [selected, setSelected] = useState(0);
    const [branch, setBranch] = useState(null);
    const [tabs] = useState(["Details", "Budgets"]);
    const [confirmationDialog, setConfirmationDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currencyData, setCurrencyData] = useState();
    const [error, setError] = useState(false);
    const [nominalType, setNominalType] = useState(null);
    const [nominalSubType, setNominalSubType] = useState(
        props.viewNominalData?.subType ?? null,
    );
    const [archived, setArchivedValue] = useState(false);
    const [nominalRef, setNominalRef] = useState();
    const [nominalId, setNominalId] = useState();
    const [nominalName, setNominalName] = useState();
    const [view, setView] = useState(props.view === "Add" ? "Add" : "View");
    const [vatCodeId, setVatCodeId] = useState();
    const [currencyCode, setCurrencyCode] = useState();
    const [controlValue, setControlValue] = useState(false);
    // Use props.viewNominalData directly to avoid object recreation
    const { data: activevatsdata = [] } = useActiveVats();
    const { data: branchList, loading: branchesLoading } = useBranches();
    const [budgets, setBudgets] = useState([]);
    const [thisYearTotal, setThisYearTotal] = useState(0);
    const [autoCalculatedMonths, setAutoCalculatedMonths] = useState(new Set());
    const isManuallyChangingTotal = useRef(false);
    /** Avoid re-applying API nominal fields when returners re-create (e.g. currencies load) — that was resetting currency edits. */
    const hydratedNominalIdRef = useRef(null);
    const { data: companyData } = useCompany();
    const { data: activeCurrencies = [] } = useActiveCurrencies();
    const companyFetched = useSelector((state) => state.company.fetched);
    const multiCurrencyModuleOn = Boolean(
        companyData?.modules?.MultiCurrency ?? companyData?.Modules?.MultiCurrency,
    );
    const canEditNominal = useAccessRight(AccountsAccessRights.ModifyNominals);
    const clientMultiCurrencyOn = Boolean(
        companyData?.modules?.ClientMultiCurrency ??
        companyData?.Modules?.ClientMultiCurrency,
    );
    const isBankBalanceSheetSubtype =
        nominalSubType === "BalanceSheet_ClientBank" ||
        nominalSubType === "BalanceSheet_DDABank";
    /** User may choose a non–base currency when general multi-currency is on, or (client multi) for client/DDA bank nominals. */
    const canPickCurrency =
        multiCurrencyModuleOn ||
        (clientMultiCurrencyOn && isBankBalanceSheetSubtype);
    const showMultiCurrencyUi =
        companyFetched &&
        canPickCurrency &&
        Array.isArray(activeCurrencies) &&
        activeCurrencies.length > 0;
    const useBaseCurrencyOnly = companyFetched && !canPickCurrency;

    const hasBalance = Number(props.viewNominalData?.balance ?? 0) !== 0;
    const isArchiveBlocked = view === "Edit" && hasBalance;
    const archiveDisabledTooltipText = isArchiveBlocked
        ? "Cannot archive nominal with balances."
        : "";

    const submitNominal = async (e) => {
        e.preventDefault();
        if (!nominalName || !nominalRef || !nominalType || !nominalSubType) {
            setError(true);
            return;
        }
        if (
            nominalType === "BalanceSheet" &&
            (nominalSubType === "BalanceSheet_OfficeBank" ||
                nominalSubType === "BalanceSheet_ClientBank") &&
            !nominalName.toLowerCase().includes("client")
        ) {
            setConfirmationDialog(true);
            return;
        }
        await nominalSubmitter();
    };

    const nominalSubmitter = async () => {
        // Prepare budgets array for API
        const budgetsToSubmit = budgets
            .filter((b) => b.year === getCurrentFiscalYear() && b.amount > 0)
            .map((b) => ({
                budgetMonth: b.monthDate,
                budgetAmount: b.amount || 0,
            }));

        const payload = {
            nominalRef: nominalRef,
            nominalName: nominalName,
            vatCodeId: vatCodeId?.vatCodeId,
            currencyCode: useBaseCurrencyOnly
                ? companyData?.baseCurrency
                : currencyCode?.currencyCode || companyData?.baseCurrency,
            type: nominalType,
            branchId: branch?.branchId,
            subType: nominalSubType,
            control: controlValue,
            archived: archived,
            budgets: budgetsToSubmit,
        };

        try {
            setIsSubmitting(true);
            let result;
            if (view === "Add") {
                result = await addNominal(payload);
            } else {
                result = await updateNominal(nominalId, payload);
            }

            if (result.success) {
                toast.success(
                    `Nominal ${view === "Add" ? "added" : "updated"} successfully!`,
                );
                // Trigger parent component to refresh
                if (props.onNominalUpdated) {
                    props.onNominalUpdated(view === "Add");
                }
                newNominalDialog();
            }
        } catch (error) {
            console.error(
                `Error ${view === "Add" ? "adding" : "updating"} nominal:`,
                error,
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const newNominalDialog = async (e) => {
        reset();
        // Clear local form state
        props.onClose();
    };

    const showError = () =>
        toast.error("Cant edit this field as this nominal has postings.");

    const changeNominalType = (e) => {
        if (view === "View") {
            return;
        }
        if (props.viewNominalData?.havePostings) return showError();
        setNominalType(e.value);
    };

    const changeNominalSubType = (e) => {
        if (view === "View") {
            return;
        }
        if (
            props.viewNominalData?.havePostings &&
            nominalType !== "ProfitAndLoss"
        ) {
            return showError();
        }
        setNominalSubType(e.value);
    };

    const changeArchivedValue = (e) => {
        if (view === "View") {
            return;
        }
        setArchivedValue(!archived);
    };

    const onChangeBranch = async (e) => {
        if (view === "View") {
            return;
        }
        try {
            setBranch(e);
        } catch (error) { }
    };

    const reset = () => {
        setNominalRef(null);
        setNominalName(null);
        setNominalType(null);
        setVatCodeId(null);
        setCurrencyCode(null);
        setArchivedValue(null);
        setNominalSubType(null);
        setControlValue(false);
        setBudgets([]);
        setThisYearTotal(0);
        setAutoCalculatedMonths(new Set());
    };

    const vatCodeReturner = React.useCallback(
        (id) => {
            let index = null;
            activevatsdata.map((item) => {
                if (item.vatCodeId === id) {
                    index = item;
                }
            });
            return index;
        },
        [activevatsdata],
    );

    const currencyCodeReturner = React.useCallback(
        (id) => {
            if (!id) return null;
            return (
                activeCurrencies.find((item) => item.currencyCode === id) || {
                    currencyCode: id,
                    display: id,
                }
            );
        },
        [activeCurrencies],
    );

    const branchCodeReturner = React.useCallback(
        (id) => branchList?.find((item) => item.branchId === id) || null,
        [branchList],
    );

    // Helper function to get fiscal year start date
    const getFiscalYearStart = React.useCallback(() => {
        if (companyData?.yearStartDate) {
            return new Date(companyData.yearStartDate);
        }
        // Default to January 1st if not set
        return new Date(new Date().getFullYear(), 0, 1);
    }, [companyData]);

    // Helper function to get current fiscal year
    const getCurrentFiscalYear = React.useCallback(() => {
        const today = new Date();
        const fiscalStart = getFiscalYearStart();
        const currentYear = today.getFullYear();

        // Check if we're past the fiscal year start month
        if (today.getMonth() >= fiscalStart.getMonth()) {
            return currentYear;
        } else {
            return currentYear - 1;
        }
    }, [getFiscalYearStart]);

    // Helper function to generate months for a fiscal year
    const generateFiscalYearMonths = React.useCallback(
        (year) => {
            const fiscalStart = getFiscalYearStart();
            const months = [];
            const startMonth = fiscalStart.getMonth();

            for (let i = 0; i < 12; i++) {
                const monthIndex = (startMonth + i) % 12;
                const yearOffset = Math.floor((startMonth + i) / 12);
                const date = new Date(year + yearOffset, monthIndex, 1);

                months.push({
                    monthName: formatDate(date, "MMM yyyy"),
                    monthDate: formatDateTimeForAPI(date),
                    year: year,
                    amount: 0,
                });
            }

            return months;
        },
        [getFiscalYearStart],
    );

    // Initialize budgets for current and previous year
    useEffect(() => {
        if (companyData?.yearStartDate && budgets.length === 0) {
            const currentYear = getCurrentFiscalYear();
            const thisYear = generateFiscalYearMonths(currentYear);
            const previousYear = generateFiscalYearMonths(currentYear - 1);
            setBudgets([...thisYear, ...previousYear]);
        }
    }, [
        companyData,
        budgets.length,
        getCurrentFiscalYear,
        generateFiscalYearMonths,
    ]);

    // Load existing budgets from viewNominalData
    useEffect(() => {
        if (
            props.viewNominalData?.budgets &&
            props.viewNominalData.budgets.length > 0 &&
            view !== "Add"
        ) {
            const existingBudgets = props.viewNominalData.budgets;
            const currentYear = getCurrentFiscalYear();
            const thisYear = generateFiscalYearMonths(currentYear);
            const previousYear = generateFiscalYearMonths(currentYear - 1);

            // Map existing budgets to the generated months
            const mappedBudgets = [...thisYear, ...previousYear].map((month) => {
                const existing = existingBudgets.find((b) =>
                    b.budgetMonth?.startsWith(month.monthDate.substring(0, 7)),
                );
                return {
                    ...month,
                    amount: existing?.budgetAmount || 0,
                };
            });

            setBudgets(mappedBudgets);
        }
    }, [
        props.viewNominalData,
        view,
        companyData,
        getCurrentFiscalYear,
        generateFiscalYearMonths,
    ]);

    useEffect(() => {
        if (isManuallyChangingTotal.current) {
            return; // Skip auto-calculation when user is manually changing total
        }
        const currentYear = getCurrentFiscalYear();
        const total = budgets
            .filter((b) => b.year === currentYear)
            .reduce((sum, b) => sum + (b.amount || 0), 0);
        setThisYearTotal(total);
    }, [budgets, getCurrentFiscalYear]);

    // Handle budget amount change
    const handleBudgetChange = (monthDate, value) => {
        if (view === "View") return;

        isManuallyChangingTotal.current = false;

        setAutoCalculatedMonths((prev) => {
            const newSet = new Set(prev);
            newSet.delete(monthDate);
            return newSet;
        });

        setBudgets((prev) =>
            prev.map((b) =>
                b.monthDate === monthDate ? { ...b, amount: value || 0 } : b,
            ),
        );
    };

    // Handle total change
    const handleTotalChange = (value) => {
        if (view === "View") return;

        isManuallyChangingTotal.current = true;

        const currentYear = getCurrentFiscalYear();
        const currentYearBudgets = budgets.filter((b) => b.year === currentYear);

        // Check if all months are empty or 0
        const allEmpty = currentYearBudgets.every(
            (b) => !b.amount || b.amount === 0,
        );

        if (allEmpty && value > 0) {
            const monthlyAmount = value / 12;
            const currentYearMonthDates = currentYearBudgets.map((b) => b.monthDate);

            setAutoCalculatedMonths((prev) => {
                const newSet = new Set(prev);
                currentYearMonthDates.forEach((monthDate) => newSet.add(monthDate));
                return newSet;
            });

            setBudgets((prev) =>
                prev.map((b) =>
                    b.year === currentYear ? { ...b, amount: monthlyAmount } : b,
                ),
            );
        } else if (value > 0) {
            const autoCalculatedCount = currentYearBudgets.filter((b) =>
                autoCalculatedMonths.has(b.monthDate),
            ).length;

            if (autoCalculatedCount > 0) {
                const manuallyEnteredSum = currentYearBudgets
                    .filter((b) => !autoCalculatedMonths.has(b.monthDate))
                    .reduce((sum, b) => sum + (b.amount || 0), 0);

                const remainingAmount = value - manuallyEnteredSum;
                const monthlyAmount = remainingAmount / autoCalculatedCount;

                setBudgets((prev) =>
                    prev.map((b) => {
                        if (
                            b.year === currentYear &&
                            autoCalculatedMonths.has(b.monthDate)
                        ) {
                            return { ...b, amount: monthlyAmount };
                        }
                        return b;
                    }),
                );
            }
        }

        setThisYearTotal(value || 0);
    };

    const handleTotalBlur = () => {
        if (view === "View") return;

        isManuallyChangingTotal.current = false;

        const currentYear = getCurrentFiscalYear();
        const total = budgets
            .filter((b) => b.year === currentYear)
            .reduce((sum, b) => sum + (b.amount || 0), 0);

        setThisYearTotal(total);
    };

    // Clear all current year budgets
    const handleClearBudgets = () => {
        if (view === "View") return;

        const currentYear = getCurrentFiscalYear();
        const currentYearMonthDates = budgets
            .filter((b) => b.year === currentYear)
            .map((b) => b.monthDate);

        setAutoCalculatedMonths((prev) => {
            const newSet = new Set(prev);
            currentYearMonthDates.forEach((monthDate) => newSet.delete(monthDate));
            return newSet;
        });

        setBudgets((prev) =>
            prev.map((b) => (b.year === currentYear ? { ...b, amount: 0 } : b)),
        );
        setThisYearTotal(0);
    };

    // Copy previous year budgets to current year
    const handleCopyFromPreviousYear = () => {
        if (view === "View") return;

        const currentYear = getCurrentFiscalYear();
        const previousYear = currentYear - 1;

        setBudgets((prev) => {
            const prevYearBudgets = prev.filter((b) => b.year === previousYear);
            const currentYearMonthDates = prev
                .filter((b) => b.year === currentYear)
                .map((b) => b.monthDate);

            setAutoCalculatedMonths((prevSet) => {
                const newSet = new Set(prevSet);
                currentYearMonthDates.forEach((monthDate) => newSet.delete(monthDate));
                return newSet;
            });

            return prev.map((b, index) => {
                if (b.year === currentYear) {
                    const correspondingPrevMonth = prevYearBudgets[index % 12];
                    return { ...b, amount: correspondingPrevMonth?.amount || 0 };
                }
                return b;
            });
        });
    };

    useEffect(() => {
        const isBsOther =
            nominalType === "BalanceSheet" && nominalSubType === "BalanceSheet_Other";
        if (!isBsOther) {
            setControlValue((prev) => (prev ? false : prev));
        }
    }, [nominalType, nominalSubType]);

    useEffect(() => {
        setvatsData(activevatsdata?.slice());
    }, [activevatsdata?.length]);

    useEffect(() => {
        setCurrencyData(activeCurrencies?.slice());
    }, [activeCurrencies?.length]);

    useEffect(() => {
        if (!showMultiCurrencyUi) {
            return;
        }
        if (view !== "Add") {
            return;
        }
        if (currencyData?.length === 1) {
            setCurrencyCode(currencyData[0]);
        }
    }, [currencyData, showMultiCurrencyUi, view]);

    useEffect(() => {
        if (view !== "Add") {
            return;
        }
        if (!showMultiCurrencyUi) {
            return;
        }
        if (!companyData?.baseCurrency || !activeCurrencies?.length) {
            return;
        }
        if (currencyCode?.currencyCode) {
            return;
        }
        const base = activeCurrencies.find(
            (c) => c.currencyCode === companyData.baseCurrency,
        );
        if (base) {
            setCurrencyCode(base);
        }
    }, [
        view,
        showMultiCurrencyUi,
        companyData?.baseCurrency,
        activeCurrencies?.length,
        currencyCode?.currencyCode,
    ]);

    useEffect(() => {
        if (vatsdata?.length === 1) {
            setVatCodeId(vatsdata[0]);
        }
    }, [vatsdata]);

    useEffect(() => {
        if (branchList?.length === 1 && !branch) {
            setBranch(branchList[0]);
        }
    }, [branchList, branch]);
    useEffect(() => {
        if (!props.viewNominalData) {
            hydratedNominalIdRef.current = null;
            return;
        }
        if (view === "Add") {
            return;
        }

        const id = props.viewNominalData.nominalId;
        if (hydratedNominalIdRef.current === id) {
            return;
        }
        hydratedNominalIdRef.current = id;

        setNominalRef(props.viewNominalData.nominalRef);
        setNominalName(props.viewNominalData.nominalName);
        setNominalId(props.viewNominalData.nominalId);
        setNominalType(props.viewNominalData.type);
        setVatCodeId(vatCodeReturner(props.viewNominalData.vatCodeId));
        setCurrencyCode(currencyCodeReturner(props.viewNominalData.currencyCode));
        setArchivedValue(props.viewNominalData.archived);
        setNominalSubType(props.viewNominalData.subType);
        setControlValue(!!props.viewNominalData.control);
        setBranch(branchCodeReturner(props.viewNominalData.branchId));
        setIsLoading(false);
    }, [
        props.viewNominalData,
        view,
        vatCodeReturner,
        currencyCodeReturner,
        branchCodeReturner,
    ]);

    /** When currency list loads after first paint, upgrade placeholder row to full currency object without clobbering user edits. */
    useEffect(() => {
        if (!props.viewNominalData || view === "Add") return;
        const apiCode = props.viewNominalData.currencyCode;
        if (!apiCode || !activeCurrencies?.length) return;
        const enriched = activeCurrencies.find((c) => c.currencyCode === apiCode);
        if (!enriched) return;
        setCurrencyCode((prev) => {
            if (prev?.currencyCode && prev.currencyCode !== apiCode) {
                return prev;
            }
            return enriched;
        });
    }, [
        activeCurrencies,
        props.viewNominalData?.nominalId,
        props.viewNominalData?.currencyCode,
        view,
    ]);

    const DetailsTab = () => (
        <div className="d-flex w-100" style={{ gap: "20%" }}>
            <div>
                <div className="d-flex flex-column">
                    <Label className="mb-1">Nominal Ref&nbsp;</Label>
                    <Input
                        className="col-6"
                        readOnly={view === "View"}
                        style={{
                            minWidth: "300px",
                            border: error && !nominalRef ? "1px solid red" : "",
                        }}
                        placeholder="Enter Nominal Ref"
                        value={nominalRef}
                        onChange={(e) => setNominalRef(e.value)}
                    />
                </div>
                <div className="d-flex flex-column mt-3">
                    <Label className="mb-1">Nominal Name&nbsp;</Label>
                    <Input
                        className="col-6"
                        readOnly={view === "View"}
                        style={{
                            minWidth: "300px",
                            border: error && !nominalName ? "1px solid red" : "",
                        }}
                        placeholder="Enter Nominal Name"
                        value={nominalName}
                        onChange={(e) => setNominalName(e.value)}
                    />

                    <div className="mt-3">
                        <Branch
                            branchList={branchList}
                            value={branch}
                            onChange={onChangeBranch}
                            controlled={true}
                            name="branchId"
                            component={"DefaultNominals"}
                            width="300px"
                            labelClass="mb-1"
                            loading={branchesLoading}
                        />
                    </div>
                    {vatsdata && (
                        <div className="d-flex flex-column mt-3">
                            <Label className="mb-1">VAT Code&nbsp;</Label>
                            <ComboBox
                                className="col-6"
                                data={activevatsdata}
                                textField={"display"}
                                filterable={true}
                                popupSettings={
                                    view === "View"
                                        ? { className: "d-none", popupClass: "hidden" }
                                        : {}
                                }
                                // onFilterChange={filterChange}
                                style={{ minWidth: "300px" }}
                                iconClassName="wa"
                                placeholder={"Search VAT Code"}
                                value={vatCodeId}
                                onChange={(e) => {
                                    if (view === "View") {
                                        return;
                                    }
                                    setVatCodeId(e.value);
                                }}
                            />
                        </div>
                    )}
                    {showMultiCurrencyUi && currencyData && (
                        <div className="d-flex flex-column mt-3">
                            <Label className="mb-1">Currency Code&nbsp;</Label>
                            <ComboBox
                                className="col-6"
                                popupSettings={
                                    view === "View"
                                        ? { className: "d-none", popupClass: "hidden" }
                                        : {}
                                }
                                data={currencyData}
                                iconClassName="wa"
                                textField="display"
                                filterable={true}
                                style={{
                                    minWidth: "300px",
                                    border:
                                        error && showMultiCurrencyUi && !currencyCode?.currencyCode
                                            ? "1px solid red"
                                            : "",
                                }}
                                placeholder={"Search Currency Code"}
                                value={currencyCode}
                                onChange={(e) => {
                                    if (view === "View") {
                                        return;
                                    }
                                    setCurrencyCode(e.value);
                                }}
                            />
                        </div>
                    )}
                    <div className="mt-3">
                        <Checkbox
                            defaultChecked={false}
                            disabled={isArchiveBlocked}
                            className={isArchiveBlocked ? "k-disabled " : ""}
                            readOnly={view === "View"}
                            value={archived}
                            onClick={changeArchivedValue}
                            label={
                                <span style={{ display: "inline-block" }}>
                                    <Tooltip
                                        anchorElement="target"
                                        position="top"
                                        parentTitle={true}
                                    >
                                        <span
                                            className={isArchiveBlocked ? "k-disabled" : ""}
                                            title={archiveDisabledTooltipText}
                                            style={{
                                                display: "inline",
                                                whiteSpace: "nowrap",
                                                pointerEvents: "auto",
                                            }}
                                        >
                                            Archived
                                        </span>
                                    </Tooltip>
                                </span>
                            }
                        />
                    </div>
                </div>
            </div>
            <div>
                <div className="mt-4">
                    <div className="d-flex radio gap-5 checkbox-label">
                        <Checkbox
                            defaultChecked={false}
                            value={controlValue}
                            readOnly={view === "View"}
                            onClick={() => {
                                if (view === "View") {
                                    return;
                                }
                                if (props.viewNominalData?.havePostings) return showError();
                                const nextChecked = !controlValue;
                                if (nextChecked) {
                                    setControlValue(true);
                                    setNominalType("BalanceSheet");
                                    setNominalSubType("BalanceSheet_Other");
                                } else {
                                    setControlValue(false);
                                }
                            }}
                            label={"Control Nominal"}
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <div className="label">
                        <Label>Nominal Types:&nbsp;</Label>
                    </div>
                    <div className={`mt-3 ${error && !nominalType ? "error" : ""}`}>
                        <RadioGroup
                            data={data}
                            readOnly={view === "View"}
                            className="gap-2 radio"
                            value={nominalType}
                            onChange={changeNominalType}
                        />
                    </div>
                </div>
                {nominalType && nominalType === "BalanceSheet" && (
                    <div className="mt-5 radio">
                        <div className="label">
                            <Label>Nominal Sub Types:&nbsp;</Label>
                        </div>
                        <div className={`mt-3 ${error && !nominalSubType ? "error" : ""}`}>
                            <RadioGroup
                                data={BalanceSheet}
                                readOnly={view === "View"}
                                value={nominalSubType}
                                className="gap-2"
                                onChange={changeNominalSubType}
                            />
                        </div>
                    </div>
                )}
                {nominalType && nominalType === "ProfitAndLoss" && (
                    <div className="mt-5 radio">
                        <div className="label">
                            <Label>Nominal Sub Types:&nbsp;</Label>
                        </div>
                        <div className={`mt-3 ${error && !nominalSubType ? "error" : ""}`}>
                            <RadioGroup
                                className="gap-2"
                                readOnly={view === "View"}
                                value={nominalSubType}
                                data={ProfitAndLoss}
                                onChange={changeNominalSubType}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const BudgetsTab = () => {
        const currentYear = getCurrentFiscalYear();
        const thisYearBudgets = budgets.filter((b) => b.year === currentYear);
        const previousYearBudgets = budgets.filter(
            (b) => b.year === currentYear - 1,
        );
        const previousYearTotal = previousYearBudgets.reduce(
            (sum, b) => sum + (b.amount || 0),
            0,
        );

        return (
            <div className="d-flex flex-column w-100">
                <div className="d-flex gap-5">
                    <div
                        style={{ minWidth: "350px", backgroundColor: "#f9f9f9" }}
                        className="rounded-3 border"
                    >
                        <div className=" d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                            <div className="fw-bold">This Year</div>
                            <div className="d-flex gap-2">
                                <Button
                                    className="bg-white"
                                    onClick={handleCopyFromPreviousYear}
                                    disabled={view === "View"}
                                >
                                    <img
                                        onClick={handleCopyFromPreviousYear}
                                        src={CopyIcon}
                                        alt="Copy Previous Year"
                                    />
                                </Button>
                                <Button
                                    className="bg-white"
                                    onClick={handleClearBudgets}
                                    disabled={view === "View"}
                                >
                                    <img src={ClearIcon} alt="Clear Icon" />
                                </Button>
                            </div>
                        </div>

                        {thisYearBudgets.map((budget) => (
                            <div
                                key={budget.monthDate}
                                className="d-flex align-items-center justify-content-between bg-white border-bottom px-3"
                                style={{ paddingTop: "2px", paddingBottom: "2px" }}
                            >
                                <Label>{budget.monthName}</Label>
                                <NumericTextBox
                                    format="n2"
                                    value={budget.amount}
                                    onChange={(e) =>
                                        handleBudgetChange(budget.monthDate, e.value)
                                    }
                                    spinners={false}
                                    placeholder="0.00"
                                    readOnly={view === "View"}
                                    className="right-align"
                                    min={0}
                                    style={{
                                        width: "120px",
                                        textAlign: "right",
                                        height: "32px",
                                    }}
                                />
                            </div>
                        ))}

                        <div
                            className="d-flex align-items-center px-3 justify-content-between"
                            style={{
                                background: "#F9F9FF",
                                paddingTop: "2px",
                                paddingBottom: "2px",
                            }}
                        >
                            <div className="fw-bold" style={{ fontSize: "14px" }}>
                                Totals
                            </div>
                            <NumericTextBox
                                format="n2"
                                value={thisYearTotal}
                                onChange={(e) => handleTotalChange(e.value)}
                                onBlur={handleTotalBlur}
                                spinners={false}
                                placeholder="0.00"
                                readOnly={view === "View"}
                                className="right-align"
                                min={0}
                                style={{
                                    width: "120px",
                                    textAlign: "right",
                                    height: "32px",
                                    fontWeight: "bold",
                                }}
                            />
                        </div>
                    </div>

                    <div className="rounded-3 border" style={{ minWidth: "350px" }}>
                        <div
                            className=" px-3 py-3 border-bottom"
                            style={{ backgroundColor: "#f9f9f9" }}
                        >
                            <div className="fw-bold">Previous Year</div>
                            <div></div>
                        </div>

                        {previousYearBudgets.map((budget) => (
                            <div
                                key={budget.monthDate}
                                className="d-flex align-items-center justify-content-between bg-white border-bottom px-3"
                                style={{ paddingTop: "2px", paddingBottom: "2px" }}
                            >
                                <Label
                                    style={{
                                        minWidth: "90px",
                                        fontSize: "13px",
                                        marginBottom: 0,
                                    }}
                                >
                                    {budget.monthName}
                                </Label>

                                <div
                                    className="d-flex align-items-center justify-content-end"
                                    style={{
                                        height: "32px",
                                    }}
                                >
                                    {budget.amount > 0 ? AmountFormat(budget.amount) : ""}
                                </div>
                            </div>
                        ))}

                        <div
                            className="d-flex align-items-center px-3 justify-content-between"
                            style={{
                                background: "#F9F9FF",
                                paddingTop: "2px",
                                paddingBottom: "2px",
                            }}
                        >
                            <div className="fw-bold" style={{ fontSize: "14px" }}>
                                Totals
                            </div>
                            <div
                                className="d-flex align-items-center justify-content-end fw-bold"
                                style={{
                                    height: "32px",
                                }}
                            >
                                {AmountFormat(previousYearTotal)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const newNominal =
        view !== "Add" && isLoading ? (
            <LoadingScreen />
        ) : (
            <Dialog
                onClose={newNominalDialog}
                title={`${view} Nominal`}
                className="add-dialog dialog-md"
            >
                <TabStrip
                    keepTabsMounted={true}
                    selected={selected}
                    onSelect={(e) => setSelected(e.selected)}
                    className="mt-4 px-5 pb-4 pagetab companytab w-100"
                >
                    {tabs.map((item, index) => {
                        return (
                            <TabStripTab
                                title={
                                    <PageTabItemTitle
                                        // className={`mb-sm-4 mb-xl-0`}
                                        title={item}
                                        index={tabs.indexOf(item)}
                                        selected={selected}
                                    />
                                }
                                key={index}
                            >
                                <div className="mt-4 w-100">
                                    {item === "Details" && DetailsTab()}
                                    {item === "Budgets" && BudgetsTab()}
                                </div>
                            </TabStripTab>
                        );
                    })}
                </TabStrip>
                <DialogActionsBar>
                    <div className="dialog-footer ">
                        <Button
                            className={`k-button common-btn bgBtnSecondary me-3`}
                            onClick={newNominalDialog}
                        >
                            {view === "View" ? "Close" : "Cancel"}
                        </Button>
                        {view !== "View" && canEditNominal && (
                            <SubmitButton
                                className="me-3"
                                onClick={submitNominal}
                                isLoading={isSubmitting}
                            >
                                Save
                            </SubmitButton>
                        )}
                        {view === "View" && canEditNominal && (
                            <Button
                                className="common-btn me-3"
                                onClick={() => setView("Edit")}
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                </DialogActionsBar>
                {confirmationDialog && (
                    <ConfirmationDialog
                        content={
                            'Are you sure to save Client Bank / DDA Bank without using the word "client" in nominal name?'
                        }
                        onClickYes={async () => {
                            setConfirmationDialog(false);
                            await nominalSubmitter();
                        }}
                        onClickNo={() => setConfirmationDialog(false)}
                        onClose={() => setConfirmationDialog(false)}
                    />
                )}
            </Dialog>
        );

    return <>{newNominal}</>;
};
export default AddNominal;
